import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import colors from 'tailwindcss/colors';

export const TAILWIND_COLORS = {
  red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-400' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-400' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-400' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-400' },
  lime: { bg: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-400' },
  green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-400' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-400' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-400' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-400' },
  sky: { bg: 'bg-sky-100', text: 'text-sky-800', border: 'border-sky-400' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-400' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-400' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-800', border: 'border-violet-400' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-400' },
  fuchsia: { bg: 'bg-fuchsia-100', text: 'text-fuchsia-800', border: 'border-fuchsia-400' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-400' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-800', border: 'border-rose-400' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-400' },
};

export const getTailwindColourShades = (colourName = 'slate') => {
  const normalized = String(colourName || 'slate').toLowerCase();
  const key = normalized === 'fuschia' ? 'fuchsia' : normalized;
  const palette = colors[TAILWIND_COLORS[key] ? key : 'slate'] || colors.slate;

  return {
    light: palette[100],
    bar: palette[100],
    text: palette[800],
    accent: palette[400],
    display: palette[700],
  };
};

/** Approximate palette height (grid + padding); used to pick open-up vs open-down. */
const PALETTE_HEIGHT_ESTIMATE = 200;

const ColorPicker = ({ value, onChange, isOpen, onToggle, disabled = false }) => {
  const rootRef = useRef(null);
  const [openUpward, setOpenUpward] = useState(false);

  const handleColorSelect = (colorName) => {
    onChange({
      colorName,
      ...TAILWIND_COLORS[colorName],
    });
    onToggle();
  };

  const getCurrentColorName = () =>
    Object.entries(TAILWIND_COLORS).find(([, classes]) => classes.bg === value?.bg)?.[0] || 'slate';

  useLayoutEffect(() => {
    if (!isOpen || disabled) return;
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const needUp =
      spaceBelow < PALETTE_HEIGHT_ESTIMATE && spaceAbove >= spaceBelow;
    setOpenUpward(needUp);
  }, [isOpen, disabled]);

  useEffect(() => {
    if (!isOpen || disabled) return;
    const onMouseDown = (e) => {
      /* Outside only: mousedown on root is stopped from bubbling (see wrapper), so we never see inside clicks here. */
      onToggle();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, disabled, onToggle]);

  const handleToggle = () => {
    if (disabled) return;
    onToggle();
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full h-[30px] rounded-md border shadow-sm flex items-center justify-between px-3 bg-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed ${
          value?.bg || 'bg-white'
        } ${value?.border || 'border-slate-300'}`}
      >
        <span className={`text-xs md:text-sm font-medium ${value?.text || 'text-slate-600'}`}>
          {getCurrentColorName()}
        </span>
        <svg
          className="w-4 h-4 shrink-0 text-slate-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div
          className={`absolute left-0 z-50 w-max min-w-[15rem] max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-lg border border-slate-200 py-1 ${
            openUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          <div className="grid grid-cols-6 gap-1 p-2">
            {Object.entries(TAILWIND_COLORS).map(([colorName, classes]) => (
              <button
                key={colorName}
                type="button"
                onClick={() => handleColorSelect(colorName)}
                className={`
                  w-8 h-8 rounded-md border transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  ${classes.bg} ${classes.border}
                `}
                title={colorName}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
